import { Outlet, NavLink, useNavigate } from 'react-router';
import {
  AppBar, Toolbar, Typography, Box, Container, Drawer, List, ListItemButton, ListItemIcon,
  ListItemText, IconButton, useMediaQuery, useTheme, Divider, Button,
} from '@mui/material';
import {
  DashboardOutlined, FactCheckOutlined, MonitorHeartOutlined, ChangeCircleOutlined,
  CalendarMonthOutlined, FolderOutlined, AlarmOutlined, RestaurantOutlined, SummarizeOutlined,
  SettingsOutlined, AdminPanelSettingsOutlined, MenuOutlined, LogoutOutlined,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLiveEvents } from '../hooks/useLiveEvents';

const NAV = [
  { to: '/', label: 'Today', icon: <DashboardOutlined /> },
  { to: '/log', label: 'Log', icon: <FactCheckOutlined /> },
  { to: '/symptoms', label: 'Symptoms', icon: <MonitorHeartOutlined /> },
  { to: '/handovers', label: 'Handovers', icon: <ChangeCircleOutlined /> },
  { to: '/calendar', label: 'Calendar', icon: <CalendarMonthOutlined /> },
  { to: '/reminders', label: 'Reminders', icon: <AlarmOutlined /> },
  { to: '/meals', label: 'Meals', icon: <RestaurantOutlined /> },
  { to: '/documents', label: 'Documents', icon: <FolderOutlined /> },
  { to: '/summary', label: 'Summary', icon: <SummarizeOutlined /> },
  { to: '/settings', label: 'Settings', icon: <SettingsOutlined /> },
] as const;

const ADMIN_NAV = [
  { to: '/admin', label: 'Admin · Users', icon: <AdminPanelSettingsOutlined /> },
] as const;

const DRAWER_WIDTH = 240;

export default function Layout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Real-time SSE subscription — invalidates TanStack Query caches on any
  // write anywhere in the family + nurse cohort. Lives for the session.
  useLiveEvents();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
          Care
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.name}
        </Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {NAV.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.to === '/'}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 1.5,
              my: 0.25,
              '&.active': { bgcolor: 'action.selected' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
        {user?.role === 'admin' && (
          <>
            <Divider sx={{ my: 1 }} />
            {ADMIN_NAV.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: 1.5,
                  my: 0.25,
                  '&.active': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          startIcon={<LogoutOutlined />}
          onClick={handleLogout}
          size="small"
          color="inherit"
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!isDesktop && (
        <AppBar position="fixed" sx={{ bgcolor: 'background.paper', color: 'text.primary' }} elevation={0}>
          <Toolbar>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
              <MenuOutlined />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Care</Typography>
          </Toolbar>
        </AppBar>
      )}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pt: { xs: 9, md: 4 },
          pb: 6,
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}
